import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { PLANS, SERVICES } from "../../data/pricing";
import { startCheckout } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { useLang } from "../../i18n/LanguageContext";
import { toast } from "sonner";
import {
  Check,
  Rocket,
  Star,
  Crown,
  Code2,
  UserRound,
  GraduationCap,
  Loader2,
  ArrowRight,
  Sparkles,
} from "lucide-react";

const ICONS = { Rocket, Star, Crown, Code2, UserRound, GraduationCap, Sparkles };

const ACCENTS = {
  slate: { text: "text-slate-300", border: "border-slate-400/30", bg: "bg-slate-400/10", btn: "bg-slate-600 hover:bg-slate-500 text-white" },
  emerald: { text: "text-emerald-300", border: "border-emerald-400/40", bg: "bg-emerald-400/10", btn: "bg-emerald-500 hover:bg-emerald-400 text-[#0F172A]" },
  blue: { text: "text-blue-300", border: "border-blue-400/40", bg: "bg-blue-500/10", btn: "bg-blue-600 hover:bg-blue-500 text-white" },
  orange: { text: "text-orange-300", border: "border-orange-400/40", bg: "bg-orange-400/10", btn: "bg-orange-500 hover:bg-orange-400 text-[#0F172A]" },
};

const useCheckout = () => {
  const [pending, setPending] = useState(null);
  const { user, setAuthOpen } = useAuth();
  const buy = async (lookup) => {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    try {
      setPending(lookup);
      await startCheckout(lookup);
    } catch {
      setPending(null);
      toast.error("Impossible d'initialiser le paiement. Réessayez.");
    }
  };
  const startFree = () => {
    if (!user) setAuthOpen(true);
    else toast.success("Vous êtes déjà inscrit ! Choisissez un forfait pour débloquer les outils IA.");
  };
  return { pending, buy, startFree };
};

const PlanCard = ({ plan, index, billing, pending, onBuy, onFree }) => {
  const { t } = useLang();
  const Icon = ICONS[plan.icon];
  const a = ACCENTS[plan.accent];
  const isFree = plan.id === "plan_free";
  const price = billing === "yearly" ? plan.yearly : plan.monthly;
  const lookup = `${plan.id}_${billing === "yearly" ? "yearly" : "monthly"}`;
  const loading = pending === lookup;

  return (
    <motion.div
      data-testid={`plan-card-${plan.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: index * 0.06 }}
      className={`relative flex flex-col rounded-2xl border p-6 backdrop-blur-xl transition-[border-color,box-shadow] duration-500 ${
        plan.recommended ? "border-blue-400/50 bg-blue-500/[0.05] glow-cyan" : "border-blue-500/15 bg-blue-400/[0.02] hover:border-blue-400/40"
      }`}
    >
      {plan.recommended && (
        <span className="absolute right-5 top-5 rounded-full border border-blue-400/40 bg-blue-500/20 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-blue-200">
          {t("pricing.recommended")}
        </span>
      )}
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl border ${a.border} ${a.bg}`}>
        <Icon size={19} className={a.text} />
      </div>
      <h3 className="mt-5 font-display text-2xl font-800 tracking-tighter text-white">{plan.name}</h3>
      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8AA0C0]">{plan.tagline}</p>

      <div className="mt-6 flex items-baseline gap-1">
        <span className="font-display text-4xl font-800 tracking-tighter text-white">${price}</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8AA0C0]">
          / {billing === "yearly" ? t("pricing.perYear") : t("pricing.perMonth")}
        </span>
      </div>
      <div className={`mt-3 inline-flex w-fit items-center gap-1.5 rounded-full border px-3 py-1 ${a.border} ${a.bg}`} data-testid={`plan-credits-${plan.id}`}>
        <Sparkles size={11} className={a.text} />
        <span className={`font-mono text-[10px] uppercase tracking-[0.12em] ${a.text}`}>{plan.credits} crédits / mois</span>
      </div>

      <ul className="mt-6 flex-1 space-y-3">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5">
            <Check size={15} className={`mt-0.5 shrink-0 ${a.text}`} />
            <span className="font-body text-sm text-[#CBD5E1]">{f}</span>
          </li>
        ))}
      </ul>

      <button
        data-testid={`plan-buy-${plan.id}`}
        onClick={() => (isFree ? onFree() : onBuy(lookup))}
        disabled={loading}
        className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-all duration-300 hover:scale-[1.02] disabled:opacity-60 ${a.btn}`}
      >
        {loading ? <Loader2 size={14} className="animate-spin" /> : null}
        {isFree ? "Commencer gratuitement" : `${t("pricing.choose")} ${plan.name}`}
      </button>
    </motion.div>
  );
};

const ServiceCard = ({ svc, index, pending, onBuy }) => {
  const { t } = useLang();
  const ts = t(`pricing.services.${svc.id}`);
  const Icon = ICONS[svc.icon];
  const a = ACCENTS[svc.accent];
  const loading = pending === svc.id;
  return (
    <motion.div
      data-testid={`service-card-${svc.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      className={`flex flex-col rounded-2xl border ${a.border} bg-blue-400/[0.02] p-7 backdrop-blur-xl`}
    >
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg border ${a.border} ${a.bg}`}>
          <Icon size={17} className={a.text} />
        </div>
        <div>
          <h4 className="font-display text-lg font-700 tracking-tighter text-white">{ts.name}</h4>
          <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#8AA0C0]">{ts.tagline}</p>
        </div>
      </div>
      <div className="mt-4 flex items-baseline gap-1">
        <span className="font-display text-3xl font-800 tracking-tighter text-white">${svc.price.toLocaleString()}</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8AA0C0]">{ts.unit}</span>
      </div>
      <ul className="mt-5 flex-1 space-y-2.5">
        {ts.features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check size={14} className={`mt-0.5 shrink-0 ${a.text}`} />
            <span className="font-body text-[13px] text-[#CBD5E1]">{f}</span>
          </li>
        ))}
      </ul>
      <button
        data-testid={`service-buy-${svc.id}`}
        onClick={() => onBuy(svc.id)}
        disabled={loading}
        className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-all duration-300 hover:scale-[1.02] disabled:opacity-60 ${a.btn}`}
      >
        {loading ? <Loader2 size={14} className="animate-spin" /> : null}
        {ts.cta}
        <ArrowRight size={13} />
      </button>
    </motion.div>
  );
};

export const Pricing = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const [billing, setBilling] = useState("monthly");
  const { pending, buy, startFree } = useCheckout();
  const { t } = useLang();

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-28 lg:py-36">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(37,99,235,0.08),transparent_55%)]" />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <div ref={ref} className="mb-12 max-w-3xl">
          <motion.span initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} className="font-mono text-xs uppercase tracking-[0.35em] text-blue-400">
            [ {t("pricing.tag")} ]
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-4 font-display text-4xl font-800 tracking-tighter text-white sm:text-5xl lg:text-6xl"
          >
            {t("pricing.title")}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mt-5 font-body text-base leading-relaxed text-[#CBD5E1] sm:text-lg"
          >
            {t("pricing.subtitle")}
          </motion.p>
        </div>

        {/* Billing toggle */}
        <div className="mb-12 inline-flex items-center gap-1 rounded-full border border-blue-500/20 bg-blue-400/5 p-1" data-testid="billing-toggle">
          {[
            { key: "monthly", label: t("pricing.monthly") },
            { key: "yearly", label: t("pricing.yearly"), note: t("pricing.yearlyNote") },
          ].map((b) => (
            <button
              key={b.key}
              data-testid={`billing-${b.key}`}
              onClick={() => setBilling(b.key)}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-all duration-300 ${
                billing === b.key ? "bg-blue-600 text-white" : "text-[#CBD5E1] hover:text-white"
              }`}
            >
              {b.label}
              {b.note && <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[9px] text-emerald-300">{b.note}</span>}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {PLANS.map((plan, i) => (
            <PlanCard key={plan.id} plan={plan} index={i} billing={billing} pending={pending} onBuy={buy} onFree={startFree} />
          ))}
        </div>

        {/* Services */}
        <div className="mt-20">
          <h3 className="mb-8 font-display text-2xl font-800 tracking-tighter text-white sm:text-3xl">
            {t("pricing.servicesTitle")}
          </h3>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {SERVICES.map((svc, i) => (
              <ServiceCard key={svc.id} svc={svc} index={i} pending={pending} onBuy={buy} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
